"use client";

import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Search, Lock } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { DashboardEmpty } from "@/components/dashboard/dashboard-empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, type SelectOption } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useConfirm } from "@/hooks/useConfirm";
import { useToast } from "@/hooks/useToast";
import { CobroFormModal, type CobroRow } from "@/components/dashboard/cobro-form-modal";

const PAGE_SIZE = 20;

type Metrics = {
  totalCobrado: number;
  totalPendiente: number;
  cantidadRegistros: number;
  promedioTicket: number;
  porMetodo: Record<string, number>;
  porMes: { mes: string; total: number }[];
};

type DatePreset = "this-month" | "last-month" | "last-3-months" | "this-year" | "custom";

const PRESET_OPTIONS: SelectOption<DatePreset>[] = [
  { value: "this-month", label: "Este mes" },
  { value: "last-month", label: "Mes anterior" },
  { value: "last-3-months", label: "Últimos 3 meses" },
  { value: "this-year", label: "Este año" },
  { value: "custom", label: "Personalizado" },
];

const STATUS_FILTER_OPTIONS: SelectOption<"all" | "pagado" | "pendiente">[] = [
  { value: "all", label: "Todos" },
  { value: "pagado", label: "Pagado" },
  { value: "pendiente", label: "Pendiente" },
];

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  efectivo: "Efectivo",
  transferencia: "Transferencia",
  tarjeta_debito: "Tarjeta de débito",
  tarjeta_credito: "Tarjeta de crédito",
  otro: "Otro",
};

function formatPrice(n: number): string {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);
}

function toDateOnly(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Rango [from, to] en formato YYYY-MM-DD para el preset elegido (excepto "custom", que usa los
 *  valores que el usuario cargó a mano). */
function rangeForPreset(preset: DatePreset, customFrom: string, customTo: string): { from: string; to: string } {
  const now = new Date();
  if (preset === "this-month") {
    return { from: toDateOnly(new Date(now.getFullYear(), now.getMonth(), 1)), to: toDateOnly(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  }
  if (preset === "last-month") {
    return { from: toDateOnly(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: toDateOnly(new Date(now.getFullYear(), now.getMonth(), 0)) };
  }
  if (preset === "last-3-months") {
    return { from: toDateOnly(new Date(now.getFullYear(), now.getMonth() - 2, 1)), to: toDateOnly(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  }
  if (preset === "this-year") {
    return { from: toDateOnly(new Date(now.getFullYear(), 0, 1)), to: toDateOnly(new Date(now.getFullYear(), 11, 31)) };
  }
  return { from: customFrom, to: customTo };
}

export function DashboardCobros() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();

  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  const [preset, setPreset] = useState<DatePreset>("this-month");
  const [customFrom, setCustomFrom] = useState(toDateOnly(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [customTo, setCustomTo] = useState(toDateOnly(new Date()));
  const [statusFilter, setStatusFilter] = useState<"all" | "pagado" | "pendiente">("all");
  const [search, setSearch] = useState("");

  const [items, setItems] = useState<CobroRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [metrics, setMetrics] = useState<Metrics | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CobroRow | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { from, to } = useMemo(() => rangeForPreset(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const loadList = useCallback(
    async (targetPage: number) => {
      const params = new URLSearchParams({ from, to, page: String(targetPage), limit: String(PAGE_SIZE) });
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await fetchWithSessionSync(`/api/cobros?${params.toString()}`, { credentials: "include" }, refresh);
      if (r.status === 403) {
        setLocked(true);
        return;
      }
      if (!r.ok) return;
      const data = (await r.json()) as { items: CobroRow[]; total: number; page: number; totalPages: number };
      setItems(data.items);
      setTotal(data.total);
      setPage(data.page);
      setTotalPages(data.totalPages);
    },
    [from, to, statusFilter, refresh],
  );

  const loadMetrics = useCallback(async () => {
    const params = new URLSearchParams({ from, to });
    const r = await fetchWithSessionSync(`/api/cobros/metrics?${params.toString()}`, { credentials: "include" }, refresh);
    if (r.status === 403) {
      setLocked(true);
      return;
    }
    if (!r.ok) return;
    setMetrics((await r.json()) as Metrics);
  }, [from, to, refresh]);

  useEffect(() => {
    setLoading(true);
    void Promise.all([loadList(1), loadMetrics()]).finally(() => setLoading(false));
  }, [loadList, loadMetrics]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(row: CobroRow) {
    setEditing(row);
    setModalOpen(true);
  }

  async function onSaved() {
    await Promise.all([loadList(page), loadMetrics()]);
  }

  async function toggleStatus(row: CobroRow) {
    setTogglingId(row.id);
    const nextStatus = row.status === "pagado" ? "pendiente" : "pagado";
    const r = await fetchWithSessionSync(
      `/api/cobros/${row.id}/status`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus }),
      },
      refresh,
    );
    setTogglingId(null);
    if (!r.ok) {
      toast.error("No pudimos actualizar el estado. Probá de nuevo.");
      return;
    }
    await Promise.all([loadList(page), loadMetrics()]);
  }

  async function deleteCobro(row: CobroRow) {
    const ok = await confirm({
      title: "Eliminar cobro",
      message: `¿Eliminar el cobro de ${row.clientName || "este cliente"} por ${formatPrice(row.amount)}? Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    setDeletingId(row.id);
    const r = await fetchWithSessionSync(`/api/cobros/${row.id}`, { method: "DELETE", credentials: "include" }, refresh);
    setDeletingId(null);
    if (!r.ok) {
      toast.error("No pudimos eliminar el cobro. Probá de nuevo.");
      return;
    }
    const nextPage = items.length === 1 && page > 1 ? page - 1 : page;
    await Promise.all([loadList(nextPage), loadMetrics()]);
  }

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.clientName.toLowerCase().includes(q));
  }, [items, search]);

  if (locked) {
    return (
      <DashboardPageShell eyebrow="Cobros" title="Cobros">
        <div className={cx(cf.dashSurface, css({ p: "8", textAlign: "center", maxW: "md", mx: "auto" }))}>
          <Lock size={28} className={css({ color: "#94a3b8", mx: "auto", mb: "3" })} aria-hidden />
          <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "2" })}>Disponible en el plan Pro</p>
          <p className={css({ fontSize: "sm", color: "#64748b", lineHeight: "1.6", mb: "5" })}>
            El módulo de Cobros está disponible para cuentas con plan Pro o superior. Actualizá tu plan para
            empezar a registrar cobros sin necesidad de turnos.
          </p>
          <Button variant="primary" onClick={() => navigate("/dashboard/subscription")}>
            Ver planes
          </Button>
        </div>
      </DashboardPageShell>
    );
  }

  const maxMes = Math.max(1, ...(metrics?.porMes.map((m) => m.total) ?? [1]));

  return (
    <DashboardPageShell
      eyebrow="Cobros"
      title="Cobros"
      description="Registrá cobros a tus pacientes sin necesidad de turnos agendados."
      actions={
        <Button variant="primary" onClick={openCreate}>
          <Plus size={16} aria-hidden />
          Registrar cobro
        </Button>
      }
    >
      {loading && !metrics ? (
        <LoadingFrame variant="light" minHeight={240} />
      ) : (
        <>
          {/* Métricas */}
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr 1fr", lg: "repeat(4, 1fr)" },
              gap: "4",
            })}
          >
            <div className={cx(cf.dashSurface, css({ p: "5", borderLeftWidth: "4px", borderLeftColor: "#16a34a" }))}>
              <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>Total cobrado</p>
              <p className={css({ fontSize: "xl", fontWeight: "bold", color: "#14532d" })}>{formatPrice(metrics?.totalCobrado ?? 0)}</p>
            </div>
            <div className={cx(cf.dashSurface, css({ p: "5", borderLeftWidth: "4px", borderLeftColor: "#d97706" }))}>
              <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>Pendiente de cobro</p>
              <p className={css({ fontSize: "xl", fontWeight: "bold", color: "#92400e" })}>{formatPrice(metrics?.totalPendiente ?? 0)}</p>
            </div>
            <div className={cx(cf.dashSurface, css({ p: "5" }))}>
              <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>Cantidad de cobros</p>
              <p className={css({ fontSize: "xl", fontWeight: "bold", color: "#0f172a" })}>{metrics?.cantidadRegistros ?? 0}</p>
            </div>
            <div className={cx(cf.dashSurface, css({ p: "5" }))}>
              <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>Ticket promedio</p>
              <p className={css({ fontSize: "xl", fontWeight: "bold", color: "#0f172a" })}>{formatPrice(metrics?.promedioTicket ?? 0)}</p>
            </div>
          </div>

          {/* Mini gráfico de últimos 6 meses */}
          {metrics && metrics.porMes.some((m) => m.total > 0) ? (
            <div className={cx(cf.dashSurface, css({ p: "5" }))}>
              <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#334155", mb: "4" })}>Cobrado por mes</p>
              <div className={css({ display: "flex", alignItems: "flex-end", gap: "3", h: "32" })}>
                {metrics.porMes.map((m) => (
                  <div key={m.mes} className={css({ flex: 1, display: "flex", flexDir: "column", alignItems: "center", gap: "2", h: "100%" })}>
                    <div className={css({ flex: 1, display: "flex", alignItems: "flex-end", w: "100%" })}>
                      <div
                        className={css({
                          w: "100%",
                          rounded: "sm",
                          bg: "token(colors.brand.500)",
                          minH: m.total > 0 ? "2" : "0",
                          transition: "height 300ms cubic-bezier(0.23, 1, 0.32, 1)",
                        })}
                        style={{ height: `${Math.max(2, (m.total / maxMes) * 100)}%` }}
                        title={formatPrice(m.total)}
                      />
                    </div>
                    <span className={css({ fontSize: "10px", color: "#94a3b8", textTransform: "capitalize" })}>{m.mes}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Filtros */}
          <div
            className={css({
              display: "flex",
              flexWrap: "wrap",
              gap: "3",
              alignItems: "flex-end",
            })}
          >
            <div className={css({ display: "flex", flexDir: "column", gap: "1", minW: "44" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Período</span>
              <Select value={preset} onChange={setPreset} options={PRESET_OPTIONS} />
            </div>
            {preset === "custom" ? (
              <>
                <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                  <span className={css({ fontSize: "xs", color: "#64748b" })}>Desde</span>
                  <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                </div>
                <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                  <span className={css({ fontSize: "xs", color: "#64748b" })}>Hasta</span>
                  <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                </div>
              </>
            ) : null}
            <div className={css({ display: "flex", flexDir: "column", gap: "1", minW: "36" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Estado</span>
              <Select value={statusFilter} onChange={setStatusFilter} options={STATUS_FILTER_OPTIONS} />
            </div>
            <div className={css({ display: "flex", flexDir: "column", gap: "1", flex: 1, minW: "48" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Buscar cliente</span>
              <div className={css({ position: "relative" })}>
                <Search size={14} aria-hidden className={css({ position: "absolute", left: "3", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" })} />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nombre del cliente…" className={css({ pl: "8" })} />
              </div>
            </div>
          </div>

          {/* Tabla */}
          {filteredItems.length === 0 ? (
            <DashboardEmpty
              title="No hay cobros en este período"
              hint="Registrá el primer cobro con el botón de arriba."
            />
          ) : (
            <div className={cx(cf.dashSurface, css({ overflowX: "auto" }))}>
              <table className={css({ width: "100%", borderCollapse: "collapse", fontSize: "sm" })}>
                <thead>
                  <tr className={css({ borderBottomWidth: "1px", borderColor: "#f1f5f9" })}>
                    {["Fecha", "Cliente", "Concepto", "Método", "Monto", "Estado", ""].map((h) => (
                      <th key={h} className={css({ textAlign: "left", px: "4", py: "3", fontSize: "xs", color: "#94a3b8", fontWeight: "semibold" })}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((row) => (
                    <tr key={row.id} className={css({ borderBottomWidth: "1px", borderColor: "#f8fafc", _hover: { bg: "#f8fafc" } })}>
                      <td className={css({ px: "4", py: "3", color: "#475569", whiteSpace: "nowrap" })}>
                        {format(new Date(row.date), "d MMM yyyy", { locale: es })}
                      </td>
                      <td className={css({ px: "4", py: "3", color: "#0f172a", fontWeight: "medium" })}>{row.clientName || "—"}</td>
                      <td className={css({ px: "4", py: "3", color: "#475569" })}>{row.description || "—"}</td>
                      <td className={css({ px: "4", py: "3", color: "#475569" })}>{PAYMENT_METHOD_LABELS[row.paymentMethod] ?? row.paymentMethod}</td>
                      <td className={css({ px: "4", py: "3", color: "#0f172a", fontWeight: "semibold", whiteSpace: "nowrap" })}>{formatPrice(row.amount)}</td>
                      <td className={css({ px: "4", py: "3" })}>
                        <button
                          type="button"
                          disabled={togglingId === row.id}
                          onClick={() => void toggleStatus(row)}
                          className={css({ border: "none", bg: "transparent", cursor: togglingId === row.id ? "wait" : "pointer", p: 0 })}
                          title="Cambiar estado"
                        >
                          <Badge tone={row.status === "pagado" ? "success" : "warning"}>
                            {row.status === "pagado" ? "Pagado" : "Pendiente"}
                          </Badge>
                        </button>
                      </td>
                      <td className={css({ px: "4", py: "3" })}>
                        <div className={css({ display: "flex", gap: "1", justifyContent: "flex-end" })}>
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            aria-label="Editar cobro"
                            className={css({ p: "1.5", rounded: "md", color: "#64748b", bg: "transparent", border: "none", cursor: "pointer", _hover: { bg: "#f1f5f9", color: "#0f172a" } })}
                          >
                            <Pencil size={14} aria-hidden />
                          </button>
                          <button
                            type="button"
                            disabled={deletingId === row.id}
                            onClick={() => void deleteCobro(row)}
                            aria-label="Eliminar cobro"
                            className={css({ p: "1.5", rounded: "md", color: "#64748b", bg: "transparent", border: "none", cursor: "pointer", _hover: { bg: "#fee2e2", color: "#b91c1c" } })}
                          >
                            <Trash2 size={14} aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          {totalPages > 1 ? (
            <div className={css({ display: "flex", justifyContent: "center", alignItems: "center", gap: "4" })}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => void loadList(page - 1)}
                className={css({ display: "flex", alignItems: "center", gap: "1", fontSize: "sm", color: "#334155", bg: "transparent", border: "none", cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1 })}
              >
                <ChevronLeft size={16} aria-hidden /> Anterior
              </button>
              <span className={css({ fontSize: "sm", color: "#64748b" })}>
                Página {page} de {totalPages} · {total} cobros
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => void loadList(page + 1)}
                className={css({ display: "flex", alignItems: "center", gap: "1", fontSize: "sm", color: "#334155", bg: "transparent", border: "none", cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? 0.4 : 1 })}
              >
                Siguiente <ChevronRight size={16} aria-hidden />
              </button>
            </div>
          ) : null}
        </>
      )}

      <CobroFormModal open={modalOpen} onClose={() => setModalOpen(false)} cobro={editing} onSaved={() => void onSaved()} />
    </DashboardPageShell>
  );
}
